import LegalPage, { Section, P, UL, Highlight } from "../components/LegalPage";

export default function GuidelinesPage() {
  return (
    <LegalPage title="Community Guidelines" lastUpdated="October 2026" badge="Community">

      <Highlight>
        Who's On? is built around one simple idea: real people, actually talking to each other.
        These guidelines exist to keep it that way — a welcoming, decent place for adults to socialise.
      </Highlight>

      <Section title="The Short Version">
        <P>Be decent. Treat people the way you'd want to be treated. Don't post anything you wouldn't want to say in a real conversation. If something feels wrong, it probably is.</P>
        <P>These guidelines apply in all public chatrooms, all Friends Rooms, and all interactions on the platform.</P>
      </Section>

      <Section title="1. Adults Only — 18+">
        <P>Who's On? is strictly for adults aged 18 and over. This isn't negotiable.</P>
        <UL items={[
          "If you are under 18, you must not use this platform.",
          "Do not discuss, solicit, or share content that involves minors in any inappropriate context.",
          "Accounts found to belong to minors will be permanently removed immediately.",
        ]} />
      </Section>

      <Section title="2. Be Respectful">
        <P>The tone of Who's On? is friendly, casual, and down-to-earth. Good conversation goes both ways. We ask that you:</P>
        <UL items={[
          "Treat other users with basic respect, even if you disagree with them.",
          "Give people the benefit of the doubt — misunderstandings happen in text.",
          "Keep the atmosphere welcoming for everyone who drops in.",
          "Remember there's a real person behind every alias.",
        ]} />
      </Section>

      <Section title="3. What's Not Allowed">
        <P>The following content and behaviour will result in moderation action, up to and including a permanent ban:</P>

        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1.5">Harassment & abuse</p>
        <UL items={[
          "Targeting or repeatedly attacking a specific user.",
          "Threats of violence or harm — even if intended as a joke.",
          "Doxxing: sharing private information about another user.",
          "Following someone between rooms to continue harassment.",
        ]} />

        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1.5">Hate speech & discrimination</p>
        <UL items={[
          "Content that attacks, degrades, or dehumanises people based on race, ethnicity, nationality, religion, gender, sexual orientation, disability, or any other protected characteristic.",
          "Slurs, derogatory language, or symbols associated with hate groups.",
        ]} />

        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1.5">Sexual content</p>
        <UL items={[
          "Explicit sexual or pornographic content of any kind.",
          "Unsolicited sexual comments directed at users.",
          "Any content that sexualises real or fictional minors.",
        ]} />

        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1.5">Spam & scams</p>
        <UL items={[
          "Flooding a room with repetitive or disruptive messages.",
          "Posting unsolicited promotional links or advertisements.",
          "Phishing, scamming, or attempting to defraud other users.",
          "Impersonating other users, public figures, or Who's On? staff.",
        ]} />

        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1.5">Safety concerns</p>
        <UL items={[
          "Content that encourages, glorifies, or provides instructions for self-harm, suicide, or violence.",
          "Sharing malware, harmful links, or content designed to compromise other users' security.",
          "Content that may constitute a criminal offence under UK law.",
        ]} />
      </Section>

      <Section title="4. Your Alias & Avatar">
        <P>Your alias and avatar are your identity on Who's On?. We use an alias system specifically to protect your privacy. A few rules apply:</P>
        <UL items={[
          "Your alias must not impersonate another person, public figure, or organisation.",
          "You may not use an alias that is itself offensive, hateful, or designed to harass.",
          "Aliases are unique — if yours is taken, choose a different one.",
          "You choose from a set of illustrated human character avatars — you may not upload personal photographs.",
        ]} />
      </Section>

      <Section title="5. Public Rooms">
        <P>Public chatrooms are open to all verified adult users during evening hours. Remember that these rooms are shared spaces — you're talking to strangers, and the conversation is visible to everyone in the room.</P>
        <UL items={[
          "Keep topics broadly appropriate for a general adult audience.",
          "Respect that not everyone shares your sense of humour.",
          "Overly political or divisive content that disrupts the room atmosphere is unwelcome.",
          "Debates and disagreements are fine — arguments and personal attacks are not.",
        ]} />
      </Section>

      <Section title="6. Friends Rooms">
        <P>Friends Rooms are private spaces you create for people you know. While more informal, the same Community Guidelines apply.</P>
        <UL items={[
          "Only invite people who have agreed to join.",
          "As creator, you take responsibility for the tone of your room.",
          "Members you add should not be harassed or pressured.",
          "If a member leaves or is removed, respect that decision.",
        ]} />
      </Section>

      <Section title="7. Evening Sessions & Message Deletion">
        <P>Public chat messages are automatically and permanently deleted at the end of every evening session (11pm UK time). This is by design — Who's On? is about live conversation, not permanent records.</P>
        <UL items={[
          "Do not share sensitive personal information in chatrooms that you would not want to exist even temporarily.",
          "Do not use Who's On? to communicate information that must be retained — use email or other permanent services for that.",
        ]} />
      </Section>

      <Section title="8. Reporting">
        <P>If you see something that violates these guidelines, please report it. You can report messages and users via the three-dot menu on any message, or by tapping a user's avatar.</P>
        <UL items={[
          "Reports are reviewed by our moderation team.",
          "The identity of the person reporting is always kept confidential.",
          "Reports that involve threats or safety concerns are flagged as high priority and reviewed first.",
          "False or malicious reports may themselves result in moderation action.",
        ]} />
      </Section>

      <Section title="9. Consequences">
        <P>Violations of these guidelines may result in:</P>
        <UL items={[
          "A formal warning recorded on your account.",
          "A temporary mute (inability to send messages for a set period).",
          "A temporary suspension (inability to log in).",
          "A permanent ban from Who's On?.",
          "Referral to law enforcement authorities where required by law.",
        ]} />
        <P>Moderation decisions are made by human administrators. Serious or repeated violations will always result in more severe action. We do not issue warnings for the most serious violations — threats, hate speech, and illegal content result in immediate permanent bans.</P>
      </Section>

      <Section title="10. A Final Note">
        <P>Who's On? exists because sometimes people just want to chat — not post, not scroll, not perform. Drop in. Have a real conversation. Be decent.</P>
        <P>If you've read this far, you're probably already the kind of person we want here. Welcome.</P>
      </Section>

    </LegalPage>
  );
}
